import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, X, CheckCircle2, Send, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  isPushSupported, loadPushStatus, subscribeToPush, unsubscribeFromPush, sendTestPush,
} from "@/lib/push";

const DISMISS_KEY = "ssp_push_optin_dismissed_until";
const DISMISS_DAYS = 14;
// Workshops older than this collapse the big push banner into a single
// status pill. Same threshold as the onboarding-checklist collapse so the
// dashboard "calms down" together rather than in stages.
const AUTO_COLLAPSE_DAYS = 14;

/**
 * Push opt-in CTA — shows above the dashboard for users who haven't yet
 * subscribed. Quietly hides when not supported, not configured, already
 * subscribed, or dismissed within the suppression window.
 *
 * For workshops older than 14 days that haven't opted in yet, we render
 * a calm one-line pill instead of the full banner — same UX pattern as
 * `OnboardingChecklist`. Click to expand for the session.
 */
export default function PushOptInPrompt() {
  const { user } = useAuth();
  const [supported] = useState(isPushSupported());
  const [status, setStatus] = useState(null); // {configured, subscribed}
  const [working, setWorking] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [autoCollapsed, setAutoCollapsed] = useState(false);
  const [expandOverride, setExpandOverride] = useState(false);

  useEffect(() => {
    if (!supported) return;
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (until && Date.now() < until) { setDismissed(true); return; }
    loadPushStatus().then(setStatus).catch(() => setStatus({ configured: false }));

    // Reuse the onboarding endpoint's auto_collapsed flag — it computes the
    // same "workshop > 14 days old?" check we want here. Free signal.
    api.get("/onboarding/state")
      .then((r) => { if (r?.data?.auto_collapsed) setAutoCollapsed(true); })
      .catch(() => { /* non-fatal */ });
  }, [supported]);

  if (user?.is_demo_user) return null;  // Boardroom demo accounts never see this nag.
  if (!supported || !status || !status.configured || status.subscribed || dismissed) return null;

  const enable = async () => {
    setWorking(true);
    try {
      await subscribeToPush();
      toast.success("Notifications on — we'll ping you when customers reply.");
      setStatus((s) => ({ ...s, subscribed: true }));
      // Fire a confirmation push so the technician sees what to expect
      try { await sendTestPush(); } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(e?.message || "Couldn't turn on notifications");
    } finally { setWorking(false); }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
    setDismissed(true);
  };

  // Calm one-line pill for workshops older than the threshold. Click to expand.
  if (autoCollapsed && !expandOverride) {
    return (
      <button
        type="button"
        onClick={() => setExpandOverride(true)}
        className="w-full text-left card-default p-3 border-primary/20 bg-primary/[0.03] hover:bg-primary/[0.06] transition-colors flex items-center gap-3"
        data-testid="push-optin-collapsed-pill"
        title="Turn on browser notifications"
      >
        <span className="inline-flex w-7 h-7 rounded-full bg-primary/15 items-center justify-center flex-shrink-0">
          <Bell className="w-3.5 h-3.5 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold">Browser notifications</span>
          <span className="ml-2 text-xs text-muted-foreground">are off — turn them on?</span>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-primary">
          Show <ChevronDown className="w-3 h-3" />
        </span>
      </button>
    );
  }

  return (
    <div
      className="card-default p-4 border-primary/40 bg-gradient-to-br from-primary/[0.08] to-primary/[0.02] flex items-start gap-3"
      data-testid="push-optin-card"
    >
      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
        <Bell className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-overline text-primary">Stay on top of replies</div>
        <div className="font-display font-bold text-base sm:text-lg leading-tight mt-1">
          Get pinged the moment a customer responds
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Browser notifications fire when a customer approves a quote, asks to speak to an advisor, or sends you a message — even when ServiceSell isn't open.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={enable}
            disabled={working}
            className="btn-primary text-sm"
            data-testid="push-optin-enable-btn"
          >
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
            Turn on notifications
          </button>
          <button
            type="button"
            onClick={dismiss}
            disabled={working}
            className="btn-outline text-sm"
            data-testid="push-optin-dismiss-btn"
          >
            Not now
          </button>
        </div>
      </div>
      <div className="flex items-start gap-1 shrink-0">
        {autoCollapsed && expandOverride && (
          <button
            type="button"
            onClick={() => setExpandOverride(false)}
            aria-label="Collapse"
            className="text-muted-foreground hover:text-foreground p-1.5 rounded-full hover:bg-muted"
            data-testid="push-optin-collapse-btn"
            title="Collapse"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide notifications prompt"
          className="text-muted-foreground hover:text-foreground p-1.5 rounded-full hover:bg-muted"
          data-testid="push-optin-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Compact toggle for the Settings page. Same logic; smaller surface.
 */
export function PushToggle() {
  const [supported] = useState(isPushSupported());
  const [status, setStatus] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!supported) return;
    loadPushStatus().then(setStatus).catch(() => {});
  }, [supported]);

  if (!supported) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="push-toggle-unsupported">
        Push notifications aren't supported by this browser.
      </div>
    );
  }
  if (!status) return null;
  if (!status.configured) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="push-toggle-unconfigured">
        Server VAPID keys aren't set — push notifications are off for everyone.
      </div>
    );
  }

  const toggle = async () => {
    setWorking(true);
    try {
      if (status.subscribed) {
        await unsubscribeFromPush();
        toast.success("Notifications turned off");
        setStatus((s) => ({ ...s, subscribed: false }));
      } else {
        await subscribeToPush();
        toast.success("Notifications on");
        setStatus((s) => ({ ...s, subscribed: true }));
      }
    } catch (e) {
      toast.error(e?.message || "Couldn't change setting");
    } finally { setWorking(false); }
  };

  const test = async () => {
    try {
      const r = await sendTestPush();
      if (r?.delivered) toast.success(`Sent to ${r.delivered} device${r.delivered === 1 ? "" : "s"}`);
      else toast.error("No active devices — make sure notifications are allowed");
    } catch { toast.error("Test failed"); }
  };

  return (
    <div className="flex items-center justify-between gap-3" data-testid="push-toggle">
      <div>
        <div className="text-sm font-semibold flex items-center gap-2">
          {status.subscribed ? <CheckCircle2 className="w-4 h-4 text-success" /> : <BellOff className="w-4 h-4 text-muted-foreground" />}
          {status.subscribed ? "Notifications on" : "Notifications off"}
        </div>
        <div className="text-xs text-muted-foreground">
          {status.subscription_count
            ? `${status.subscription_count} device${status.subscription_count === 1 ? "" : "s"} subscribed`
            : "Tap to enable on this device"}
        </div>
      </div>
      <div className="flex gap-2">
        {status.subscribed && (
          <button type="button" onClick={test} className="btn-outline text-xs" data-testid="push-toggle-test-btn">
            <Send className="w-3 h-3" /> Test
          </button>
        )}
        <button
          type="button"
          onClick={toggle}
          disabled={working}
          className={status.subscribed ? "btn-outline text-xs" : "btn-primary text-xs"}
          data-testid="push-toggle-btn"
        >
          {working ? <Loader2 className="w-3 h-3 animate-spin" /> : status.subscribed ? <BellOff className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
          {status.subscribed ? "Turn off" : "Turn on"}
        </button>
      </div>
    </div>
  );
}
